import { useTranslation } from "react-i18next";
import { useCurrency } from "@/hooks/useCurrency";
import type { WizardData } from "@/pages/CreateCard";
import { Separator } from "@/components/ui/separator";

const VAT_RATE = 0.05; // 5%

interface Props {
  data: WizardData;
  templatePrice: number | null;
}

const StepCheckout = ({ data, templatePrice }: Props) => {
  const { t } = useTranslation();
  const currency = useCurrency();

  const price = templatePrice || 0;
  const vat = Math.round(price * VAT_RATE * 100) / 100;
  const total = Math.round((price + vat) * 100) / 100;

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
        {t("wizard.checkout.title", { defaultValue: "Checkout" })}
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        {t("wizard.checkout.subtitle", { defaultValue: "Review your order before payment" })}
      </p>

      <div className="border border-border rounded-2xl overflow-hidden">
        {/* Card preview */}
        {data.templateUrl && (
          <div className="flex items-center gap-3 p-4 bg-muted/30">
            <img
              src={data.templateUrl}
              alt="Selected card"
              className="w-16 h-20 rounded-lg object-cover border border-border"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {data.title || data.recipientName ? `Card for ${data.recipientName}` : "Group Card"}
              </p>
              <p className="text-xs text-muted-foreground capitalize">{data.occasion}</p>
            </div>
          </div>
        )}

        {/* Price breakdown */}
        <div className="p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {t("wizard.checkout.designPrice", { defaultValue: "Card Design" })}
            </span>
            <span className="font-medium text-foreground">
              {currency} {price.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {t("wizard.checkout.vat", { defaultValue: "VAT (5%)" })}
            </span>
            <span className="font-medium text-foreground">
              {currency} {vat.toFixed(2)}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between text-base font-bold">
            <span className="text-foreground">
              {t("wizard.checkout.total", { defaultValue: "Total" })}
            </span>
            <span className="text-foreground">
              {currency} {total.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center mt-4">
        {t("wizard.checkout.note", { defaultValue: "You'll be redirected to a secure payment page. Your card will be saved as draft until payment is completed." })}
      </p>
    </div>
  );
};

export default StepCheckout;
